import { isNil } from 'lodash';
import { CSSProperties, useCallback, useContext, useState } from 'react';
import ReactModal from 'react-modal';
import ArrowMoveDownIcon from '@shared/assets/icons/v2/arrow-move-down.svg';
import ExitIcon from '@shared/assets/icons/v2/exit.svg';
import { MBView } from '@shared/components/MBView';
import { FileUpload } from '@shared/components/blocks';
import { Carousel } from '@shared/components/carousel';
import { MBModalButton } from '@shared/components/modal';
import { ColorThemeContext } from '@shared/styles';
import { API } from '@shared/types/api';
import { useBoolean } from '@shared/util/hooks';
import { isTouchDevice } from '@shared/util/isTouchDevice';
import { downloadFullSizeImageUri } from '@shared/util/media/image';
import { MBZoomableImage } from '../../zoomable';
import { MBImagePreviewProps } from '../ImagePreview';
import { Styles, styles } from './styles';

type MBImagePreviewModalProps = MBImagePreviewProps & { visible: boolean };

interface TitleProps {
  children: string | number;
  color: string;
}

function ImagePreviewTitle({ children: title, color }: TitleProps) {
  return (
    <h2
      style={
        {
          ...styles.title,
          color,
        } as CSSProperties
      }
    >
      <span style={styles.titleInside as CSSProperties}>{title}</span>
    </h2>
  );
}

/*
    Image preview on web.
*/
export function MBImagePreviewModal({
  onClose,
  showIndex,
  images,
  visible,
}: MBImagePreviewModalProps) {
  const colors = useContext(ColorThemeContext);
  const [activeIndex, setActiveIndex] = useState(showIndex);
  const [isZoomed, onZoomStart, onZoomEnd] = useBoolean(false);
  const activeImage = isNil(activeIndex) ? undefined : images?.[activeIndex];
  const filename = activeImage?.name;

  const handleDownload = () => {
    if (isNil(activeImage)) {
      return;
    }
    const anchor = document.createElement('a');
    anchor.href = downloadFullSizeImageUri(activeImage.url);
    anchor.target = '_blank';
    anchor.download = filename || '';
    anchor.click();
  };

  const onIndexChange = (i: number) => {
    setActiveIndex(i);
  };

  const shouldHaveHorizontalMargins = images && images.length > 1 && !isTouchDevice;

  const renderItem = useCallback(
    (image: FileUpload | API.ChatAttachmentFragment) => (
      <MBZoomableImage
        image={image}
        shouldHaveHorizontalMargins={shouldHaveHorizontalMargins}
        onZoomStart={onZoomStart}
        onZoomEnd={onZoomEnd}
      />
    ),
    [shouldHaveHorizontalMargins, onZoomStart, onZoomEnd],
  );

  return (
    <ReactModal
      isOpen={visible}
      onRequestClose={onClose}
      style={{
        overlay: styles.overlay,
        content: styles.content,
      }}
    >
      <MBModalButton
        color={colors.button.foreground}
        style={Styles.download}
        icon={ArrowMoveDownIcon}
        onPress={handleDownload}
      />
      {filename && (
        <ImagePreviewTitle color={colors.button.foreground}>{filename}</ImagePreviewTitle>
      )}
      <MBModalButton
        color={colors.button.foreground}
        style={Styles.close}
        icon={ExitIcon}
        onPress={onClose}
      />
      <MBView center style={Styles.carouselContainer}>
        {images && (
          <Carousel<FileUpload | API.ChatAttachmentFragment>
            data={images}
            itemWidthPercentage={1}
            style={{
              flex: 1,
              width: '100%',
              height: '100%',
              position: 'relative',
            }}
            initialActiveIndex={showIndex}
            paginationColor={colors.button.foreground}
            onChangeActiveIndex={onIndexChange}
            showNavButtons={!isTouchDevice || isZoomed}
            renderItem={renderItem}
          />
        )}
      </MBView>
    </ReactModal>
  );
}
